import api from './client';
import { FamilyMember } from '../store/familyStore';

export interface CalendarEvent {
  id: string;
  family_id: string;
  created_by_id: string;
  name: string;
  description: string | null;
  location: string | null;
  start_datetime: string;
  end_datetime: string;
  all_day: boolean;
  color: string;
  recurring: boolean;
  recurrence_rule: string | null;
  created_at: string;
  updated_at: string;
  members: FamilyMember[];
}

export interface CalendarEventCreate {
  name: string;
  description?: string | null;
  location?: string | null;
  start_datetime: string;
  end_datetime: string;
  all_day?: boolean;
  color?: string;
  recurring?: boolean;
  recurrence_rule?: string | null;
  member_ids?: string[];
}

export interface CalendarEventUpdate {
  name?: string;
  description?: string | null;
  location?: string | null;
  start_datetime?: string;
  end_datetime?: string;
  all_day?: boolean;
  color?: string;
  recurring?: boolean;
  recurrence_rule?: string | null;
  member_ids?: string[];
}

export interface SpecialDateEvent {
  id: string;
  name: string;
  date_type: string;
  start_datetime: string;
  end_datetime: string;
  all_day: boolean;
  color: string;
  recurring: boolean;
  person_name: string;
  person_id: string | null;
  is_special_date: true;
}

export const calendarApi = {
  // Get events for a family
  getFamilyEvents: async (
    familyId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<CalendarEvent[]> => {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('start_date', startDate.toISOString());
    }
    if (endDate) {
      params.append('end_date', endDate.toISOString());
    }
    
    const response = await api.get(
      `/calendar/family/${familyId}/events?${params.toString()}`
    );
    return response.data;
  },

  // Get a specific event
  getEvent: async (familyId: string, eventId: string): Promise<CalendarEvent> => {
    const response = await api.get(
      `/calendar/family/${familyId}/events/${eventId}`
    );
    return response.data;
  },

  // Create a new event
  createEvent: async (
    familyId: string,
    data: CalendarEventCreate
  ): Promise<CalendarEvent> => {
    const response = await api.post(
      `/calendar/family/${familyId}/events`,
      data
    );
    return response.data;
  },

  // Update an event
  updateEvent: async (
    familyId: string,
    eventId: string,
    data: CalendarEventUpdate
  ): Promise<CalendarEvent> => {
    const response = await api.put(
      `/calendar/family/${familyId}/events/${eventId}`,
      data
    );
    return response.data;
  },

  // Delete an event
  deleteEvent: async (familyId: string, eventId: string): Promise<void> => {
    await api.delete(`/calendar/family/${familyId}/events/${eventId}`);
  },

  // Get all events from user's families
  getMyEvents: async (startDate?: Date, endDate?: Date): Promise<CalendarEvent[]> => {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('start_date', startDate.toISOString());
    }
    if (endDate) {
      params.append('end_date', endDate.toISOString());
    }
    
    const response = await api.get(`/calendar/my-events?${params.toString()}`);
    return response.data;
  },

  // Get special dates (birthdays, anniversaries) as calendar events
  getSpecialDates: async (familyId: string, year?: number): Promise<SpecialDateEvent[]> => {
    const params = new URLSearchParams();
    if (year) {
      params.append('year', year.toString());
    }
    
    const response = await api.get(`/calendar/family/${familyId}/special-dates?${params.toString()}`);
    return response.data;
  },
};

